// ./src/pages/private/EventGuestsPage/GuestDrawer.tsx
//
// Add / edit one event guest (or team member). You either LINK an existing
// contact — in which case the guest references it live (name/contact are NOT
// copied, so editing the contact flows to every event they attend) — or type a
// one-off guest in. Captures role, RSVP status, party size, seat, star, dietary
// notes, and group assignment (one per set). Registration answers live in a
// separate drawer. Uses the shared AppDrawer + form fields.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Stack, Group, Divider, Text, Button, Paper, Box, Collapse, UnstyledButton, PasswordInput, Badge, Input } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { OpResult } from "@sdflc/api-helpers";

import {
  EventGuest,
  EventGuestInput,
  EventGuestService,
  EventTicket,
  EventTicketService,
  Person,
  PersonService,
  EVENT_GUEST_ROLE,
  EVENT_RSVP_STATUS,
  EVENT_RSVP_STATUS_LABELS,
  RsvpGroupService,
  EventGuestTimeSlotAttendance,
  listAllPages,
} from "@/utils/api";
import { formatSlotLabel, isSlotBookable } from "@/utils/eventDates";
import { useToast, useModalShortcuts, useRsvpGroups, useEvent, useFormSnapshot, SHORTCUT_HINTS } from "@/hooks";
import { getFormErrors, formatErrors, formatDateTime, refusalReason } from "@/utils";
import { buildEventPublicUrl } from "@/utils/eventPublicUrl";
import { guestOverridePatch } from "@/utils/guestOverrides";
import {
  AppDrawer,
  AppIcon,
  TextField,
  PhoneField,
  TextAreaField,
  SelectField,
  NumberField,
  SwitchField,
  QuickCreateDrawer,
  CheckableMultiSelect,
} from "@/components/generic";
import { useCrewAccess, useDefaultPhoneCountry } from "@/contexts";
import { CrewSessionService } from "@/utils/api/CrewSessionService";
import { canSetGuestPhoto } from "@/utils/guestPhoto";
import { MediaImageField } from "@/components/generic";
import type { MediaImageRef } from "@/components/generic";
import { GuestContactOverrides } from "@/components/features/EventGuests/GuestContactOverrides";
import { PersonPicker } from "@/components/features/EventGuests/PersonPicker";
import { GuestGroupAssignments } from "@/components/features/EventGuests/GuestGroupAssignments";
import { LinkedAdultsSection } from "@/components/features/EventGuests/LinkedAdultsSection";
import { LinkedContactSection } from "@/components/features/EventPartners/LinkedContactSection/LinkedContactSection";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_FOR_GUEST_EDITORS, CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";
import { RsvpGroupMembersDrawer } from "./RsvpGroupMembersDrawer";



// Server error names that map to a visible input: known → inline under the field,
// anything else → generic, in the alert above Save.
/** The form as the unsaved-changes check compares it (#123): the groups are a SET, so unticking and re-ticking one is no change. */
const comparableForm = (f: GuestForm) => ({ ...f, guestGroupIds: new Set(f.guestGroupIds) });

const KNOWN_FORM_FIELDS = ["personId", "avatarUploadedFileId", "firstName", "lastName", "email", "phone", "guestRole", "rsvpStatus", "partySize", "seatAssignment", "isStarred", "isAttendee", "notifyOfRsvp", "notifyOfTicketsPurchase", "notifyOfMessages", "notifyOfPartnerApplications", "notifyOfVolunteerApplications", "notifyOfSignupPayments", "dietaryRestrictions", "messageToHost", "bio", "website", "socialUrl1", "socialUrl2", "socialUrl3", "jobTitle", "company", "guestGroupIds", "rsvpGroupId", "isGroupPrimary", "rsvpGroupSize", "bibNumber", "accessPassword", "timeSlotIds"];

interface GuestForm {
  personId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  guestRole: string;
  rsvpStatus: string;
  partySize: number | null;
  seatAssignment: string;
  isStarred: boolean;
  isAttendee: boolean;
  notifyOfRsvp: boolean;
  notifyOfTicketsPurchase: boolean;
  notifyOfMessages: boolean;
  notifyOfPartnerApplications: boolean;
  notifyOfVolunteerApplications: boolean;
  notifyOfSignupPayments: boolean;
  dietaryRestrictions: string;
  messageToHost: string;
  bio: string;
  /** The guest's own photo for this event (#69); null = none (the contact's is shown). */
  photo: MediaImageRef | null;
  website: string;
  socialUrl1: string;
  socialUrl2: string;
  socialUrl3: string;
  /** Event-specific job title / company for the event website (empty = the contact's). */
  jobTitle: string;
  company: string;
  guestGroupIds: string[];
  rsvpGroupId: string | null;
  isGroupPrimary: boolean;
  rsvpGroupSize: number | null;
  bibNumber: string;
  accessPassword: string;
}

const emptyForm = (defaultRole: string): GuestForm => ({
  personId: null,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  guestRole: defaultRole,
  rsvpStatus: EVENT_RSVP_STATUS.INVITED,
  partySize: 1,
  seatAssignment: "",
  isStarred: false,
  // Guests are always counted heads; crew default to NOT counted (host opts them in).
  isAttendee: defaultRole === EVENT_GUEST_ROLE.GUEST,
  notifyOfRsvp: false,
  notifyOfTicketsPurchase: false,
  notifyOfMessages: false,
  notifyOfPartnerApplications: false,
  notifyOfVolunteerApplications: false,
  notifyOfSignupPayments: false,
  dietaryRestrictions: "",
  messageToHost: "",
  bio: "",
  photo: null,
  website: "",
  socialUrl1: "",
  socialUrl2: "",
  socialUrl3: "",
  jobTitle: "",
  company: "",
  guestGroupIds: [],
  rsvpGroupId: null,
  isGroupPrimary: false,
  rsvpGroupSize: null,
  bibNumber: "",
  accessPassword: "",
});

const toForm = (g: EventGuest, defaultRole: string): GuestForm => ({
  personId: g.personId ?? null,
  firstName: g.firstName ?? "",
  lastName: g.lastName ?? "",
  email: g.email ?? "",
  phone: g.phone ?? "",
  guestRole: g.guestRole ?? defaultRole,
  rsvpStatus: g.rsvpStatus ?? EVENT_RSVP_STATUS.INVITED,
  partySize: g.partySize ?? 1,
  seatAssignment: g.seatAssignment ?? "",
  isStarred: !!g.isStarred,
  isAttendee: g.isAttendee ?? true,
  notifyOfRsvp: !!g.notifyOfRsvp,
  notifyOfTicketsPurchase: !!g.notifyOfTicketsPurchase,
  notifyOfMessages: !!g.notifyOfMessages,
  notifyOfPartnerApplications: !!g.notifyOfPartnerApplications,
  notifyOfVolunteerApplications: !!g.notifyOfVolunteerApplications,
  notifyOfSignupPayments: !!g.notifyOfSignupPayments,
  dietaryRestrictions: g.dietaryRestrictions ?? "",
  messageToHost: g.messageToHost ?? "",
  bio: g.bio ?? "",
  // ⓘ Keep the id even when the url is missing, so a save never drops a photo it could not preview.
  photo: g.avatarUploadedFileId ? { uploadedFileId: g.avatarUploadedFileId, url: g.avatarUploadedFile?.fileUrl ?? g.avatarUploadedFile?.thumbnailUrl ?? null } : null,
  website: g.website ?? "",
  socialUrl1: g.socialUrl1 ?? "",
  socialUrl2: g.socialUrl2 ?? "",
  socialUrl3: g.socialUrl3 ?? "",
  jobTitle: g.jobTitle ?? "",
  company: g.company ?? "",
  guestGroupIds: (g.guestGroups ?? []).map((gr) => gr.id).filter((id): id is string => !!id),
  rsvpGroupId: g.rsvpGroupId ?? null,
  isGroupPrimary: !!g.isGroupPrimary,
  rsvpGroupSize: g.rsvpGroupSize ?? null,
  bibNumber: g.bibNumber ?? "",
  accessPassword: g.accessPassword ?? "",
});

export interface GuestDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  guest: EventGuest | null;
  /** Default role for a new row (guest vs a team role). */
  defaultRole: string;
  /** Role choices shown in the drawer. */
  roleOptions: { value: string; label: string }[];
  /** Whether the role selector is shown (hidden on the Guests page). */
  showRole?: boolean;
  /** Contact ids already on the event — blocked in the picker to prevent duplicates. */
  existingPersonIds: string[];
  /** Called after a successful save with the saved guest (for the row highlight). */
  onSaved: (guest?: EventGuest) => void;
  /** Open the separate registration-answers drawer for this guest (edit mode). */
  onOpenAnswers?: (guest: EventGuest) => void;
  /** Open the notes drawer for this guest (edit mode). */
  onOpenNotes?: (guest: EventGuest) => void;
  /** ROADMAP A4 — true on a recurring event: the drawer offers the guest's Dates. */
  recurringDates: boolean;
  /** The event's dates with who is booked on each (the guest list's attendance read). */
  dateSlots: EventGuestTimeSlotAttendance[];
  /** Whether `dateSlots` has loaded — until it has, the Dates field is locked and never sent. */
  dateSlotsLoaded: boolean;
  /** The event timezone, to name the dates. */
  timezone?: string | null;
}

export const GuestDrawer: React.FC<GuestDrawerProps> = ({
  opened,
  onClose,
  eventId,
  guest,
  defaultRole,
  roleOptions,
  showRole = true,
  existingPersonIds,
  onSaved,
  onOpenAnswers,
  onOpenNotes,
  recurringDates,
  dateSlots,
  dateSlotsLoaded,
  timezone,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const toast = useToast();
  const [form, setForm] = useState<GuestForm>(() => emptyForm(defaultRole));
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError],
  );
  const [contactEditOpen, setContactEditOpen] = useState(false);
  const [pickerCreateOpen, setPickerCreateOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);

  const { groups: rsvpGroups, reload: reloadRsvpGroups } = useRsvpGroups(eventId);
  const rsvpGroupOptions = useMemo(
    () => rsvpGroups.map((gr) => ({ value: gr.id as string, label: gr.name ?? "—" })),
    [rsvpGroups]
  );
  const rsvpGroupName = useMemo(
    () => rsvpGroups.find((gr) => gr.id === form.rsvpGroupId)?.name ?? null,
    [rsvpGroups, form.rsvpGroupId]
  );
  // Create-in-place for the RSVP group (a name-only entity → QuickCreateDrawer,
  // not a full drawer). The created group is reloaded into the list and selected.
  const [rsvpGroupCreateOpen, setRsvpGroupCreateOpen] = useState(false);
  // #142 — a crew member adds an RSVP group only with `guests.edit` (the server's rule).
  const canCreateRsvpGroup = useCreateInPlace(CREATE_FOR_GUEST_EDITORS);
  // #142 — a contact lands in the caller's OWN account: a crew member's would be out of the event's reach, so their
  // typed-in guest keeps its own name and contact instead (an unlinked guest does).
  const canCreateContact = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);

  const isEdit = !!guest?.id;

  // ROADMAP A4 — the guest's booked dates. Seeded ONCE per opened guest, and only after the dates have loaded: a field
  // seeded from a list that had not arrived yet would read "no dates" and a save would un-book every one of them.
  const [dateIds, setDateIds] = useState<string[]>([]);
  const initialDateIdsRef = useRef<string[]>([]);
  const seededDatesFor = useRef<string | null>(null);

  useEffect(() => {
    if (!opened) {
      seededDatesFor.current = null;

      return;
    }

    const key = guest?.id ?? "__new__";
    if (!recurringDates || !dateSlotsLoaded || seededDatesFor.current === key) {
      return;
    }

    const booked = guest?.id ? dateSlots.filter((s) => (s.guestIds ?? []).includes(guest.id as string)).map((s) => s.id) : [];
    setDateIds(booked);
    initialDateIdsRef.current = booked;
    seededDatesFor.current = key;
  }, [opened, guest?.id, recurringDates, dateSlotsLoaded, dateSlots]);

  // Dates still open for booking, plus any the guest already holds (so keeping one never reads as dropping it).
  const dateOptions = useMemo(
    () =>
      dateSlots
        .filter((s) => isSlotBookable(s) || dateIds.includes(s.id))
        .map((s) => ({ value: s.id, label: formatSlotLabel(s, timezone) })),
    [dateSlots, dateIds, timezone]
  );

  const seededGuestRef = useRef<string | null>(null);

  useEffect(() => {
    if (!opened) {
      clearSnapshot();
      seededGuestRef.current = null;

      return;
    }

    // Seeded when the guest arrives (an edit stage opened by URL loads it after the drawer opens). A REFRESHED guest —
    // the page re-reads it, e.g. after its linked contact was edited — keeps what the user has typed (#125); another
    // guest replaces the form.
    const key = guest?.id ?? "__new__";
    setForm(reseed(guest ? toForm(guest, defaultRole) : emptyForm(defaultRole), key));
    setCrewHasPassword(!!guest?.hasCrewPassword);

    if (seededGuestRef.current === key) {
      return;
    }

    seededGuestRef.current = key;
    setContactEditOpen(false);
    setPickerCreateOpen(false);
    // Reveal the IDs & access section up front only when it already holds data.
    setShowAdvanced(!!(guest?.bibNumber || guest?.accessPassword || guest?.hasCrewPassword));
    // Crew access state (docs/CREW-ACCESS.md) — reset for each guest.
    setCrewPassword("");
    // Reveal the public-profile section when the guest already has bio / links.
    setShowProfile(!!(guest?.avatarUploadedFileId || guest?.bio || guest?.website || guest?.socialUrl1 || guest?.socialUrl2 || guest?.socialUrl3 || guest?.jobTitle || guest?.company));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, guest]);

  // Stage 7 Part B — "Purchased by": for an ATTENDEE (role guest), resolve the BUYER of the order(s)
  // that issued their ticket(s) — the order is the buyer↔attendee link (e.g. the kid's emergency
  // contact = the parent who bought their ticket). Read-only; hidden for a self-purchase (the attendee
  // bought their own ticket) and when the guest holds no ticket. Distinct buyers only (usually one).
  const [purchasers, setPurchasers] = useState<Array<{ personId: string | null; name: string | null; email: string | null }>>([]);

  useEffect(() => {
    let alive = true;
    setPurchasers([]);

    if (!opened || !guest?.id || guest.guestRole !== EVENT_GUEST_ROLE.GUEST) {
      return;
    }

    void listAllPages(EventTicketService.list, { eventGuestId: [guest.id], eventId: [eventId] }).then((res) => {
      if (!alive) {
        return;
      }

      const tickets = (res.getData?.() ?? []) as EventTicket[];
      const seen = new Set<string>();
      const out: Array<{ personId: string | null; name: string | null; email: string | null }> = [];
      for (const tk of tickets) {
        const ord = tk.ticketOrder;
        const buyerPid = ord?.personId ? String(ord.personId) : null;
        const buyerName = ord?.buyerName || null;
        if (!ord || (!buyerPid && !buyerName)) {
          continue;
        }
        // Skip a self-purchase — the attendee bought their own ticket, so there's no "other" buyer.
        if (buyerPid && guest.personId && buyerPid === String(guest.personId)) {
          continue;
        }

        const key = buyerPid || `name:${buyerName}`;
        if (seen.has(key)) {
          continue;
        }

        seen.add(key);
        out.push({ personId: buyerPid, name: buyerName, email: ord.buyerEmail || null });
      }

      setPurchasers(out);
    });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, guest?.id, eventId]);

  const handleChange = useCallback((name: string | undefined, value: unknown) => {
    if (!name) return;
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  // Default parse region for the phone field — the brand's space.default_country (fallback US).
  const defaultPhoneCountry = useDefaultPhoneCountry();
  // #69 — may this caller set a guest's photo? The host, or a co-host signed in with an ACCOUNT (a password crew link
  // never) — the server's rule, mirrored (`utils/guestPhoto.ts`); the server still refuses anyone else on the field.
  // ⓘ The app-wide access is for the event in the URL. The Create Event wizard opens this drawer on a draft its own
  //   account is creating (`/events/create` names no event), so there the caller — signed in, not a crew link — owns it.
  const crewAccess = useCrewAccess();
  const accessIsForThisEvent = !!eventId && crewAccess.eventId === eventId;
  // ⓘ A crew link counts only for ITS event, as on the server — a leftover token from another event must not take the
  //   photo away from the account that owns this one.
  const viaPasswordCrew = CrewSessionService.hasSession() && CrewSessionService.getEventId() === eventId;
  // #69 — the guest removed every photo of themselves from the event site; until they show it again, nobody else sets one.
  const photoHiddenByGuest = !!guest?.photoHiddenAt;
  const canSetPhoto = canSetGuestPhoto({
    isOwner: accessIsForThisEvent ? crewAccess.isOwner : !viaPasswordCrew,
    viaPasswordCrew,
    role: accessIsForThisEvent ? crewAccess.role : null,
  });

  // Host-issued cross-device restore: copy the guest's personal access link
  // (…/e/<slug>?inv=<token>). Ensures the token, builds the public URL, copies it.
  const { event: linkEvent } = useEvent(eventId);
  const [linkBusy, setLinkBusy] = useState(false);
  const copyAccessLink = useCallback(async () => {
    if (!guest?.id) return;

    setLinkBusy(true);
    const res = await EventGuestService.ensureInviteLink(guest.id);
    setLinkBusy(false);

    const token = res.getDataFirst?.()?.token ?? (res as unknown as { data?: { token?: string }[] })?.data?.[0]?.token;
    if ((res.didFail?.() ?? res.code !== 0) || !token) {
      toast.error(refusalReason(res) || t("events:guests.accessLinkFailed", { defaultValue: "Could not create the access link." }));
      return;
    }

    const url = buildEventPublicUrl(linkEvent ?? undefined, { query: { inv: token }, fallbackId: eventId });
    if (!url) {
      toast.error(t("events:guests.accessLinkNoUrl", { defaultValue: "This event has no public link yet." }));
      return;
    }

    try {
      await navigator.clipboard.writeText(url);

      // #62 P6(d4) — a DRAFT's site answers "not found" to everyone but its crew, so the link works only once published —
      // said here rather than letting the host send a guest a link that looks dead.
      if (linkEvent?.eventStatus === "draft") {
        toast.info(t("events:guests.accessLinkCopiedDraft", { defaultValue: "Access link copied. It opens once you publish the event — until then guests see “not found”." }));

        return;
      }

      toast.success(t("events:guests.accessLinkCopied", { defaultValue: "Access link copied — share it so the guest can manage their RSVP on any device." }));
    } catch {
      toast.info(url);
    }
  }, [guest?.id, linkEvent, eventId, toast, t]);

  // Crew access (docs/CREW-ACCESS.md): a non-account crew member opens the event
  // via {appOrigin}/crew-access?inv=<token> and this password. Setting a password
  // (server-hashed) + ensuring the invite token, then copying the ready-made link.
  const [crewPassword, setCrewPassword] = useState("");
  const [crewBusy, setCrewBusy] = useState(false);
  const [crewHasPassword, setCrewHasPassword] = useState(false);

  // #123 — the form + a typed, not-yet-set crew password: Esc / a click outside / Cancel ask before discarding
  // them. The Dates field is compared separately (`datesChanged`, below): it is seeded LATER, once the dates
  // have loaded, and re-seeding the whole snapshot then would forget an edit made in the meantime. The crew password
  // always opens empty, so any text in it is a change.
  const { isDirty: isFormChanged, reseed, clear: clearSnapshot } = useFormSnapshot(form, comparableForm);
  const isFormDirty = isFormChanged || !!crewPassword.trim();

  const isCrewRole = form.guestRole !== EVENT_GUEST_ROLE.GUEST && form.guestRole !== "buyer";

  const saveCrewAccess = useCallback(
    async (opts?: { clear?: boolean }) => {
      if (!guest?.id) return;

      setCrewBusy(true);
      const res = await EventGuestService.setCrewAccess({
        eventGuestId: guest.id,
        password: opts?.clear ? undefined : crewPassword.trim() || undefined,
        clearPassword: opts?.clear ? true : undefined,
      });
      setCrewBusy(false);

      const data = res.getDataFirst?.() ?? (res as unknown as { data?: { token?: string; hasPassword?: boolean }[] })?.data?.[0];
      if ((res.didFail?.() ?? res.code !== 0) || !data?.token) {
        toast.error(refusalReason(res) || t("events:crew.accessFailed", { defaultValue: "Could not update crew access." }));
        return;
      }

      setCrewHasPassword(!!data.hasPassword);

      if (opts?.clear) {
        setCrewPassword("");
        toast.success(t("events:crew.passwordRemoved", { defaultValue: "Crew password removed." }));
        return;
      }

      const url = `${window.location.origin}/crew-access?inv=${encodeURIComponent(data.token)}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(
          t("events:crew.linkCopied", {
            defaultValue: "Crew sign-in link copied — share it with the password so they can open the event.",
          })
        );
      } catch {
        toast.info(url);
      }
      setCrewPassword("");
    },
    [guest?.id, crewPassword, toast, t]
  );

  // Linking a contact stores the reference + a read-only info card. We do NOT copy
  // the name/contact into the guest — the guest resolves them live from the person.
  const handlePickPerson = useCallback((person: Person | null) => {
    setForm((f) => ({ ...f, personId: person?.id ?? null }));
  }, []);

  // Owner, 2026-11-02 — what a linked guest can still do for itself: details for this one event, or another contact.
  const [changingContact, setChangingContact] = useState(false);

  // ⓘ A crew member's row is read back with their USER's name in these fields, so it has no overrides to show or clear.
  const canOverride = isEdit && !!form.personId && !guest?.userId;
  // Only while it is still the contact the guest was opened with: another one's values would be the wrong placeholders.
  const contactShown = form.personId && form.personId === guest?.personId ? guest?.person ?? null : null;

  // #40 — a guest whose restored place is held pending payment may only be DENIED, which withdraws the
  // place; the API refuses every other status (`guardStatusWriteAgainstHolds`). The full list would
  // offer statuses the save then rejects — the drawer, the list and the server must state ONE rule.
  const awaitingPayment = !!guest?.awaitingPayment;
  const rsvpOptions = useMemo(
    () =>
      awaitingPayment
        ? [
            {
              value: EVENT_RSVP_STATUS.PENDING,
              label: t("events:guests.awaitingPayment", { defaultValue: "Awaiting payment" }),
            },
            {
              value: EVENT_RSVP_STATUS.DENIED,
              label: t("events:guests.withdrawPlaceOption", { defaultValue: "Denied (withdraws the place)" }),
            },
          ]
        : Object.values(EVENT_RSVP_STATUS).map((v) => ({ value: v, label: EVENT_RSVP_STATUS_LABELS[v] })),
    [awaitingPayment, t]
  );

  const linked = !!form.personId;

  // Linked guests are always valid (identity comes from the contact); one-off
  // guests need at least a name or email.
  const canSave = (linked || !!form.firstName.trim() || !!form.lastName.trim() || !!form.email.trim()) && !saving;

  // ROADMAP A4 — dates are sent only when seeded AND changed: the server checks each newly added date's room and voids a
  // dropped date's coupons, and an unchanged list must not re-run any of that. The same answer makes the drawer dirty.
  const datesChanged =
    recurringDates &&
    seededDatesFor.current !== null &&
    [...dateIds].sort().join("|") !== [...initialDateIdsRef.current].sort().join("|");
  const isDirty = isFormDirty || datesChanged;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setSubmitError(null);

    // Every guest is also a contact: a typed-in guest gets a contact created for
    // them (so the info is reusable on the next event) and is linked to it — when this person may make one HERE.
    let personId = form.personId;
    if (!isEdit && !personId && canCreateContact && (form.firstName.trim() || form.lastName.trim() || form.email.trim())) {
      const pRes = await PersonService.create({
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
      });
      if (!pRes.didSucceed()) {
        setSaving(false);
        toast.error(refusalReason(pRes) || t("events:guests.contactCreateFailed", { defaultValue: "Could not create the contact." }));
        return;
      }
      personId = (pRes.data as Person[])?.[0]?.id ?? null;
    }

    const linkedNow = !!personId;
    const input: EventGuestInput = {
      guestRole: form.guestRole,
      rsvpStatus: form.rsvpStatus,
      // A guest in an RSVP group is a single head — the group carries the count.
      partySize: form.rsvpGroupId ? 1 : form.partySize ?? 1,
      seatAssignment: form.seatAssignment.trim() || null,
      isStarred: form.isStarred,
      // Guests always count; crew count only if the host opts them in.
      isAttendee: form.guestRole === EVENT_GUEST_ROLE.GUEST ? true : form.isAttendee,
      // Crew notification prefs only apply to crew; force off for the guest role.
      notifyOfRsvp: form.guestRole === EVENT_GUEST_ROLE.GUEST ? false : form.notifyOfRsvp,
      notifyOfTicketsPurchase: form.guestRole === EVENT_GUEST_ROLE.GUEST ? false : form.notifyOfTicketsPurchase,
      notifyOfMessages: form.guestRole === EVENT_GUEST_ROLE.GUEST ? false : form.notifyOfMessages,
      // ⚠ Cleared for a plain GUEST, like its siblings — only crew can subscribe to crew mail, and
      // a flag left set on someone demoted out of crew keeps emailing them.
      notifyOfPartnerApplications:
        form.guestRole === EVENT_GUEST_ROLE.GUEST ? false : form.notifyOfPartnerApplications,
      notifyOfVolunteerApplications:
        form.guestRole === EVENT_GUEST_ROLE.GUEST ? false : form.notifyOfVolunteerApplications,
      notifyOfSignupPayments: form.guestRole === EVENT_GUEST_ROLE.GUEST ? false : form.notifyOfSignupPayments,
      dietaryRestrictions: form.dietaryRestrictions.trim() || null,
      messageToHost: form.messageToHost.trim() || null,
      bio: form.bio.trim() || null,
      // #69 — only a caller who may set it sends it (the server refuses anyone else; an organizer's save leaves it as is).
      // A guest who removed their photo from the site keeps it removed (the server refuses a new one), so nothing is sent.
      ...(canSetPhoto && !photoHiddenByGuest ? { avatarUploadedFileId: form.photo?.uploadedFileId ?? null } : {}),
      website: form.website.trim() || null,
      socialUrl1: form.socialUrl1.trim() || null,
      socialUrl2: form.socialUrl2.trim() || null,
      socialUrl3: form.socialUrl3.trim() || null,
      jobTitle: form.jobTitle.trim() || null,
      company: form.company.trim() || null,
      bibNumber: form.bibNumber.trim() || null,
      accessPassword: form.accessPassword.trim() || null,
      guestGroupIds: form.guestGroupIds,
      rsvpGroupId: form.rsvpGroupId,
      isGroupPrimary: form.rsvpGroupId ? form.isGroupPrimary : false,
      // The per-guest household cap (max people they may RSVP for); null = event default.
      rsvpGroupSize: form.rsvpGroupSize && form.rsvpGroupSize > 0 ? form.rsvpGroupSize : null,
    };
    if (datesChanged) {
      input.timeSlotIds = dateIds;
    }
    // A linked guest leaves name/contact null so the contact's live values are used. Only a guest with no contact keeps its
    // own, and an existing one sends what the host changed under "For this event only".
    if (!linkedNow) {
      input.firstName = form.firstName.trim() || null;
      input.lastName = form.lastName.trim() || null;
      input.email = form.email.trim() || null;
      input.phone = form.phone.trim() || null;
    } else if (isEdit && !guest?.userId) {
      Object.assign(input, guestOverridePatch(form, guest));
    }

    if (isEdit && personId && personId !== guest?.personId) {
      input.personId = personId;
    }

    const result = isEdit
      ? await EventGuestService.update({ id: guest!.id! }, input)
      : await EventGuestService.create({ ...input, eventId, personId });

    setSaving(false);
    if (!result.didSucceed()) {
      // Keep the OpResult: field errors go under their inputs, the rest above Save.
      setSubmitError(result);

      return;
    }
    onSaved((result.data as EventGuest[])?.[0]);
    onClose();
  };

  // Don't let ⌘S here fire while a nested drawer (contact editor / create) is open —
  // that drawer owns the shortcut and its own save.
  useModalShortcuts({ onConfirm: save, canConfirm: canSave, enabled: opened && !contactEditOpen && !pickerCreateOpen && !membersOpen });

  const title = isEdit
    ? t("events:guests.editTitle", { defaultValue: "Edit guest" })
    : t("events:guests.addTitle", { defaultValue: "Add guest" });

  // Approval workflow (#37) — READ-ONLY here. The verdict itself is taken from the guest list row
  // (Approve / Deny buttons) and confirmed in its own URL-routed drawer, so this drawer never opens
  // another one on top of itself. What stays is the record of what was decided: a host reopening a
  // guest wants to remember why they said no, and whether the guest was told.
  const wasDecided =
    isEdit &&
    !!guest?.approvedAt &&
    (guest?.rsvpStatus === EVENT_RSVP_STATUS.APPROVED || guest?.rsvpStatus === EVENT_RSVP_STATUS.DENIED);

  const decidedLine = useMemo(() => {
    if (!wasDecided || !guest) {
      return "";
    }
    const verb =
      guest.rsvpStatus === EVENT_RSVP_STATUS.APPROVED
        ? t("events:guests.decidedApproved", { defaultValue: "Approved" })
        : t("events:guests.decidedDenied", { defaultValue: "Denied" });
    const who = guest.decidedByLabel
      ? t("events:guests.decidedBy", { defaultValue: " by {{name}}", name: guest.decidedByLabel })
      : "";
    const when = guest.approvedAt ? ` · ${formatDateTime(guest.approvedAt, "datetime")}` : "";

    return `${verb}${who}${when}`;
  }, [wasDecided, guest, t]);

  const reviewBlock = wasDecided ? (
    <Alert
      color={guest?.rsvpStatus === EVENT_RSVP_STATUS.APPROVED ? "green" : "orange"}
      variant="light"
      icon={<AppIcon iconName={guest?.rsvpStatus === EVENT_RSVP_STATUS.APPROVED ? "Check" : "X"} size={16} />}
    >
      <Stack gap={4}>
        <Text size="sm" fw={600}>
          {decidedLine}
        </Text>
        {guest?.decisionReason && (
          <>
            <Text size="sm">{guest.decisionReason}</Text>
            <Text size="xs" c="dimmed">
              {guest.decisionReasonShared
                ? t("events:guests.reasonWasShared", { defaultValue: "This reason was emailed to the guest." })
                : t("events:guests.reasonWasInternal", { defaultValue: "Internal note — the guest was not told why." })}
            </Text>
          </>
        )}
      </Stack>
    </Alert>
  ) : null;

  // The guest's linked directory contact — the SAME reusable card the "Purchased by" block + the
  // transaction/partner drawers use (name + avatar/Gravatar + contact rows + Edit). `onEditingChange`
  // pauses this drawer's shortcuts while the nested contact editor is open; editing it refreshes the
  // guest list (the linked name may have changed).
  const linkedCard = form.personId && (
    <Stack gap="xs">
      <LinkedContactSection
        kind="person"
        id={form.personId}
        canEdit={isEdit}
        onEditingChange={setContactEditOpen}
        onChanged={() => onSaved(guest ?? undefined)}
      />
      <Text size="md" c="dimmed">
        {t("events:guests.linkedNote", {
          defaultValue: "Uses this contact's details, unless this event has its own below. Updating the contact updates every event that uses them.",
        })}
      </Text>

      {canOverride && (
        <GuestContactOverrides
          values={form}
          contact={contactShown}
          onChange={handleChange}
          onClear={() => setForm((f) => ({ ...f, firstName: "", lastName: "", email: "", phone: "" }))}
          errors={fieldErrors}
          defaultPhoneCountry={defaultPhoneCountry}
          changingContact={changingContact}
          onToggleChangeContact={() => setChangingContact((open) => !open)}
        >
          {changingContact && (
            <PersonPicker
              label={t("events:guests.changeContactLabel", { defaultValue: "Another contact" })}
              description={t("events:guests.changeContactHint", {
                defaultValue: "This guest becomes that person on this event. Tickets and orders keep the name they were bought under.",
              })}
              placeholder={t("events:guests.linkContactPh", { defaultValue: "Search contacts…" })}
              value={form.personId}
              onSelect={(person) => {
                if (person) {
                  handlePickPerson(person);
                  setChangingContact(false);
                }
              }}
              excludeIds={existingPersonIds}
              onCreateOpenChange={setPickerCreateOpen}
              error={fieldErrors.personId}
            />
          )}
        </GuestContactOverrides>
      )}
    </Stack>
  );

  const manualFields = (
    <>
      <Group gap="sm" grow align="flex-start">
        <TextField name="firstName" label={t("events:guests.firstName", { defaultValue: "First name" })} value={form.firstName} onChange={handleChange}
  error={fieldErrors.firstName}
/>
        <TextField name="lastName" label={t("events:guests.lastName", { defaultValue: "Last name" })} value={form.lastName} onChange={handleChange}
  error={fieldErrors.lastName}
/>
      </Group>
      <Group gap="sm" grow align="flex-start">
        <TextField name="email" label={t("events:guests.email", { defaultValue: "Email" })} value={form.email} onChange={handleChange}
  error={fieldErrors.email}
/>
        <PhoneField name="phone" label={t("events:guests.phone", { defaultValue: "Phone" })} value={form.phone} onChange={handleChange}
  defaultCountry={defaultPhoneCountry}
  error={fieldErrors.phone}
/>
      </Group>
    </>
  );

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={save}
      confirmLabel={t("common:buttons.save", { defaultValue: "Save" })}
      cancelLabel={t("common:buttons.cancel", { defaultValue: "Cancel" })}
      confirmDisabled={!canSave}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        {reviewBlock}

        {!isEdit && (
          <PersonPicker
            label={t("events:guests.linkContact", { defaultValue: "Link a contact (optional)" })}
            description={t("events:guests.linkContactHint", {
              defaultValue: "Pick someone from your contacts, or just type a one-off guest below.",
            })}
            placeholder={t("events:guests.linkContactPh", { defaultValue: "Search contacts…" })}
            value={form.personId}
            onSelect={handlePickPerson}
            excludeIds={existingPersonIds}
            onCreateOpenChange={setPickerCreateOpen}
            error={fieldErrors.personId}
          />
        )}

        {/* Editing an existing guest hides the contact picker, so a server refusal on
            `personId` has no input to land on — show it here instead of losing it. */}
        {isEdit && fieldErrors.personId && !changingContact ? <Input.Error>{fieldErrors.personId}</Input.Error> : null}

        {linked ? (
          linkedCard
        ) : (
          <>
            {!isEdit && <Divider label={t("events:guests.orEnterManually", { defaultValue: "Or add a one-off guest" })} labelPosition="center" />}
            {manualFields}
          </>
        )}

        <Divider />

        {/* Stage 7 Part B — the reverse buyer↔attendee link: who bought this attendee's ticket. */}
        {isEdit && guest?.guestRole === EVENT_GUEST_ROLE.GUEST && purchasers.length > 0 && (
          <>
            <Stack gap="xs">
              <Text fw={600} size="sm">
                {t("events:guests.purchasedBy", { defaultValue: "Purchased by" })}
              </Text>
              {purchasers.map((b, i) => (
                <Box key={b.personId || b.name || i}>
                  {b.personId ? (
                    <LinkedContactSection kind="person" id={b.personId} />
                  ) : (
                    <Text size="sm">{b.name || b.email || t("events:guests.aBuyer", { defaultValue: "A buyer" })}</Text>
                  )}
                </Box>
              ))}
              <Text size="xs" c="dimmed">
                {t("events:guests.purchasedByHint", {
                  defaultValue: "The buyer of the order that issued this attendee's ticket — e.g. their emergency contact.",
                })}
              </Text>
            </Stack>

            <Divider />
          </>
        )}

        <Group gap="sm" grow align="flex-start">
          {showRole && (
            <SelectField
              name="guestRole"
              label={t("events:guests.role", { defaultValue: "Role" })}
              data={roleOptions}
              value={form.guestRole}
              onChange={handleChange}
              allowDeselect={false}
              error={fieldErrors.guestRole}
            />
          )}
          <SelectField
            name="rsvpStatus"
            label={t("events:guests.rsvpStatus", { defaultValue: "RSVP status" })}
            data={rsvpOptions}
            value={form.rsvpStatus}
            onChange={handleChange}
            allowDeselect={false}
            error={fieldErrors.rsvpStatus}
          />
        </Group>

        <Group gap="sm" grow align="flex-start">
          <NumberField
            name="partySize"
            label={t("events:guests.partySize", { defaultValue: "Party size" })}
            description={
              form.rsvpGroupId
                ? t("events:guests.partySizeGroupHint", { defaultValue: "In an RSVP group — counted as 1; the group holds the headcount." })
                : t("events:guests.partySizeHint", { defaultValue: "Total heads incl. this guest." })
            }
            value={form.rsvpGroupId ? 1 : form.partySize}
            onChange={handleChange}
            min={1}
            allowDecimal={false}
            disabled={!!form.rsvpGroupId}
            error={fieldErrors.partySize}
          />
          <TextField
            name="seatAssignment"
            label={t("events:guests.seat", { defaultValue: "Seat / table" })}
            placeholder={t("events:guests.seatPh", { defaultValue: "e.g. Table 5" })}
            value={form.seatAssignment}
            onChange={handleChange}
            error={fieldErrors.seatAssignment}
          />
        </Group>

        {recurringDates && (
          <Stack gap={4}>
            <CheckableMultiSelect
              label={t("events:guests.dates", { defaultValue: "Dates" })}
              placeholder={t("events:guests.datesPlaceholder", { defaultValue: "Not booked on any date" })}
              data={dateOptions}
              value={dateIds}
              onChange={setDateIds}
              disabled={!dateSlotsLoaded}
              searchable
            />
            {fieldErrors.timeSlotIds ? (
              <Input.Error>{fieldErrors.timeSlotIds}</Input.Error>
            ) : (
              <Text size="xs" c="dimmed">
                {t("events:guests.datesHint", {
                  defaultValue: "A new date needs room for this guest. Removing a date voids its unused RSVP vouchers.",
                })}
              </Text>
            )}
          </Stack>
        )}

        {/* Crew can optionally count as an attendee (and appear on the Guests list).
            Guests always count, so the toggle is hidden for the guest role. */}
        {/* ⓘ Not for a stall's voucher redeemer (2026-10-18): they work one stall — never a head, never emailed the event's activity. */}
        {/* ⓘ Nor a ticket buyer (#286): their tickets are the heads, and they run nothing. */}
        {isCrewRole && form.guestRole !== EVENT_GUEST_ROLE.VOUCHER_REDEEMER && (
          <SwitchField
            name="isAttendee"
            label={t("events:guests.isAttendee", { defaultValue: "Also attending" })}
            description={t("events:guests.isAttendeeHint", {
              defaultValue: "On = counts as a head and shows on the Guests list too, not just Crew.",
            })}
            checked={form.isAttendee}
            onChange={handleChange}
            error={fieldErrors.isAttendee}
          />
        )}

        {/* Crew notification preferences — who gets emailed about activity. Hidden for
            the guest role (guests don't receive host notifications), and for a buyer. */}
        {isCrewRole && form.guestRole !== EVENT_GUEST_ROLE.VOUCHER_REDEEMER && (
          <>
            <SwitchField
              name="notifyOfRsvp"
              label={t("events:guests.notifyOfRsvp", { defaultValue: "Notify of RSVPs" })}
              description={t("events:guests.notifyOfRsvpHint", {
                defaultValue: "Email this person the full details whenever a guest RSVPs or updates their RSVP.",
              })}
              checked={form.notifyOfRsvp}
              onChange={handleChange}
              error={fieldErrors.notifyOfRsvp}
            />

            <SwitchField
              name="notifyOfTicketsPurchase"
              label={t("events:guests.notifyOfTickets", { defaultValue: "Notify of ticket purchases" })}
              description={t("events:guests.notifyOfTicketsHint", {
                defaultValue: "Email this person when tickets are purchased for this event.",
              })}
              checked={form.notifyOfTicketsPurchase}
              onChange={handleChange}
              error={fieldErrors.notifyOfTicketsPurchase}
            />

            <SwitchField
              name="notifyOfMessages"
              label={t("events:guests.notifyOfMessages", { defaultValue: "Notify of incoming messages" })}
              description={t("events:guests.notifyOfMessagesHint", {
                defaultValue: "Email this person when a guest sends a message from the Contact Us form.",
              })}
              checked={form.notifyOfMessages}
              onChange={handleChange}
              error={fieldErrors.notifyOfMessages}
            />

            {/* ⚠ Its OWN opt-in, not folded into the ticket one. Whoever watches box-office revenue
                is rarely whoever chases sponsors, and mail somebody cannot act on is how a person
                mutes the whole channel. */}
            <SwitchField
              name="notifyOfPartnerApplications"
              label={t("events:guests.notifyOfPartnerApplications", { defaultValue: "Notify of partner applications" })}
              description={t("events:guests.notifyOfPartnerApplicationsHint", {
                defaultValue: "Email this person when a sponsor or partner applies to this event.",
              })}
              checked={form.notifyOfPartnerApplications}
              onChange={handleChange}
              error={fieldErrors.notifyOfPartnerApplications}
            />

            {/* #A4.6 — its own opt-in too: whoever coordinates volunteers is rarely whoever chases sponsors. */}
            <SwitchField
              name="notifyOfVolunteerApplications"
              label={t("events:guests.notifyOfVolunteerApplications", { defaultValue: "Notify of volunteer sign-ups" })}
              description={t("events:guests.notifyOfVolunteerApplicationsHint", {
                defaultValue: "Email this person when somebody signs up to volunteer, or a volunteer withdraws from a shift.",
              })}
              checked={form.notifyOfVolunteerApplications}
              onChange={handleChange}
              error={fieldErrors.notifyOfVolunteerApplications}
            />

            {/* #149 — its own opt-in: guests paying for sign-up list items online. */}
            <SwitchField
              name="notifyOfSignupPayments"
              label={t("events:guests.notifyOfSignupPayments", { defaultValue: "Notify of sign-up list payments" })}
              description={t("events:guests.notifyOfSignupPaymentsHint", {
                defaultValue: "Email this person when a guest pays for sign-up list items online — who bought what, how many, and their notes.",
              })}
              checked={form.notifyOfSignupPayments}
              onChange={handleChange}
              error={fieldErrors.notifyOfSignupPayments}
            />
          </>
        )}

        <SwitchField
          name="isStarred"
          label={t("events:guests.starred", { defaultValue: "Star this guest" })}
          description={t("events:guests.starredHint", { defaultValue: "Flag VIPs or people to follow up with." })}
          checked={form.isStarred}
          onChange={handleChange}
          error={fieldErrors.isStarred}
        />

        <TextAreaField
          name="dietaryRestrictions"
          label={t("events:guests.dietary", { defaultValue: "Dietary notes (this event)" })}
          value={form.dietaryRestrictions}
          onChange={handleChange}
          autosize
          minRows={1}
          maxRows={3}
          error={fieldErrors.dietaryRestrictions}
        />

        <TextAreaField
          name="messageToHost"
          label={t("events:guests.messageToHost", { defaultValue: "Message from the guest" })}
          description={t("events:guests.messageToHostHint", { defaultValue: "What the guest wrote when they RSVP'd." })}
          value={form.messageToHost}
          onChange={handleChange}
          autosize
          minRows={1}
          maxRows={4}
          error={fieldErrors.messageToHost}
        />

        <Divider />

        {/* Public profile — bio + links shown on the event website (e.g. a speaker
            showcase). Event-specific; empty fields fall back to the linked contact. */}
        <div>
          <UnstyledButton onClick={() => setShowProfile((s) => !s)} style={{ width: "100%" }}>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600} size="md">
                {t("events:guests.profile", { defaultValue: "Public profile (this event)" })}
              </Text>
              <AppIcon iconName={showProfile ? "ChevronUp" : "ChevronDown"} size={16} />
            </Group>
          </UnstyledButton>
          <Collapse in={showProfile}>
            <Stack gap="sm" mt="sm">
              {/* #69 — the guest's photo for THIS event: only the host, or a co-host signed in with an account — and not
                  once the guest has removed their photo from the site themselves (their choice wins). */}
              {photoHiddenByGuest ? (
                <Alert color="gray" variant="light" icon={<AppIcon iconName="EyeOff" size={18} />}>
                  <Text size="md">
                    {t("events:guests.photoHiddenByGuest", {
                      defaultValue:
                        "This guest removed their photo from the event site on {{when}}. The site shows their initials, and only they can show a photo again, from their RSVP.",
                      when: formatDateTime(guest?.photoHiddenAt ?? "", "date"),
                    })}
                  </Text>
                </Alert>
              ) : canSetPhoto ? (
                <MediaImageField
                  intent="avatar"
                  allowUrl={false}
                  value={form.photo}
                  onChange={(img) => setForm((f) => ({ ...f, photo: img }))}
                  label={t("events:guests.photo", { defaultValue: "Photo (this event)" })}
                  description={t("events:guests.photoHint", {
                    defaultValue: "Shown wherever this event shows its guests or team. Leave empty to use the contact's photo.",
                  })}
                  addLabel={t("events:guests.photoAdd", { defaultValue: "Add photo" })}
                  error={fieldErrors.avatarUploadedFileId}
                  disabled={saving}
                />
              ) : null}
              {/* The people blocks' "Head of Design, Acme" line — a speaker's title and company, for this event. */}
              <TextField
                name="jobTitle"
                label={t("events:guests.jobTitle", { defaultValue: "Title" })}
                description={t("events:guests.jobTitleHint", { defaultValue: "Shown under their name on the event website. Leave empty to use the contact's." })}
                placeholder={linked && guest?.person?.jobTitle ? guest.person.jobTitle : t("events:guests.jobTitlePh", { defaultValue: "e.g. Head of Design" })}
                value={form.jobTitle}
                onChange={handleChange}
                error={fieldErrors.jobTitle}
              />
              <TextField
                name="company"
                label={t("events:guests.company", { defaultValue: "Company" })}
                placeholder={linked && guest?.person?.company ? guest.person.company : t("events:guests.companyPh", { defaultValue: "e.g. Acme" })}
                value={form.company}
                onChange={handleChange}
                error={fieldErrors.company}
              />
              <TextAreaField
                name="bio"
                label={t("events:guests.bio", { defaultValue: "Bio" })}
                description={t("events:guests.bioHint", { defaultValue: "A short blurb for the event website. Leave empty to use the contact's bio." })}
                placeholder={linked ? guest?.person?.bio ?? undefined : undefined}
                value={form.bio}
                onChange={handleChange}
                autosize
                minRows={2}
                maxRows={6}
                error={fieldErrors.bio}
              />
              <TextField
                name="website"
                label={t("events:guests.website", { defaultValue: "Website" })}
                placeholder={linked && guest?.person?.personalWebsite ? guest.person.personalWebsite : "https://…"}
                value={form.website}
                onChange={handleChange}
                leftSection={<AppIcon iconName="World" size={14} />}
                error={fieldErrors.website}
              />
              <TextField
                name="socialUrl1"
                label={t("events:guests.social1", { defaultValue: "Social link 1" })}
                placeholder={linked && guest?.person?.socialUrl1 ? guest.person.socialUrl1 : "https://…"}
                value={form.socialUrl1}
                onChange={handleChange}
                leftSection={<AppIcon iconName="Link" size={14} />}
                error={fieldErrors.socialUrl1}
              />
              <TextField
                name="socialUrl2"
                label={t("events:guests.social2", { defaultValue: "Social link 2" })}
                placeholder={linked && guest?.person?.socialUrl2 ? guest.person.socialUrl2 : "https://…"}
                value={form.socialUrl2}
                onChange={handleChange}
                leftSection={<AppIcon iconName="Link" size={14} />}
                error={fieldErrors.socialUrl2}
              />
              <TextField
                name="socialUrl3"
                label={t("events:guests.social3", { defaultValue: "Social link 3" })}
                placeholder={linked && guest?.person?.socialUrl3 ? guest.person.socialUrl3 : "https://…"}
                value={form.socialUrl3}
                onChange={handleChange}
                leftSection={<AppIcon iconName="Link" size={14} />}
                error={fieldErrors.socialUrl3}
              />
            </Stack>
          </Collapse>
        </div>

        <Divider />

        {/* IDs & access — bib (races), page password, and the generated guest code.
            Collapsed by default since most events don't need it. */}
        <div>
          <UnstyledButton onClick={() => setShowAdvanced((s) => !s)} style={{ width: "100%" }}>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600} size="md">
                {t("events:guests.idsAccess", { defaultValue: "IDs & access" })}
              </Text>
              <AppIcon iconName={showAdvanced ? "ChevronUp" : "ChevronDown"} size={16} />
            </Group>
          </UnstyledButton>
          <Collapse in={showAdvanced}>
            <Stack gap="sm" mt="sm">
              {isEdit && guest?.guestCode && (
                <TextField
                  name="guestCode"
                  label={t("events:guests.guestCode", { defaultValue: "Guest code" })}
                  description={t("events:guests.guestCodeHint", { defaultValue: "Auto-generated scannable code for check-in." })}
                  value={guest.guestCode}
                  onChange={() => {}}
                  disabled
                />
              )}
              {isEdit && guest?.id && (
                <Button
                  variant="light"
                  leftSection={<AppIcon iconName="Link" size={16} />}
                  onClick={copyAccessLink}
                  loading={linkBusy}
                  style={{ alignSelf: "flex-start" }}
                >
                  {t("events:guests.copyAccessLink", { defaultValue: "Copy RSVP access link" })}
                </Button>
              )}

              {/* Crew sign-in access — only for crew roles (docs/CREW-ACCESS.md). */}
              {isEdit && guest?.id && isCrewRole && (
                <Paper withBorder radius="md" p="sm">
                  <Stack gap="xs">
                    <Group gap="xs" justify="space-between">
                      <Group gap={6}>
                        <AppIcon iconName="Shield" size={16} />
                        <Text fw={600}>
                          {t("events:crew.accessTitle", { defaultValue: "Crew sign-in access" })}
                        </Text>
                      </Group>
                      {crewHasPassword ? (
                        <Badge color="teal" variant="light">
                          {t("events:crew.passwordSet", { defaultValue: "Password set" })}
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light">
                          {t("events:crew.noPassword", { defaultValue: "No password" })}
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" c="dimmed">
                      {t("events:crew.accessHint", {
                        defaultValue:
                          "Give a crew member with no account a password to open this event. Sharing the link copies a ready-made sign-in link — send it with the password.",
                      })}
                    </Text>
                    <PasswordInput
                      value={crewPassword}
                      onChange={(e) => setCrewPassword(e.currentTarget.value)}
                      placeholder={
                        crewHasPassword
                          ? t("events:crew.passwordPhSet", { defaultValue: "Enter a new password to replace it" })
                          : t("events:crew.passwordPh", { defaultValue: "Set a crew password" })
                      }
                    />
                    <Group gap="xs">
                      <Button
                        variant="light"
                        leftSection={<AppIcon iconName="Link" size={16} />}
                        onClick={() => saveCrewAccess()}
                        loading={crewBusy}
                      >
                        {crewPassword.trim()
                          ? t("events:crew.saveAndCopy", { defaultValue: "Set password & copy link" })
                          : t("events:crew.copyLink", { defaultValue: "Copy crew sign-in link" })}
                      </Button>
                      {crewHasPassword && (
                        <Button
                          variant="subtle"
                          color="red"
                          onClick={() => saveCrewAccess({ clear: true })}
                          loading={crewBusy}
                        >
                          {t("events:crew.removePassword", { defaultValue: "Remove password" })}
                        </Button>
                      )}
                    </Group>
                  </Stack>
                </Paper>
              )}

              <TextField
                name="bibNumber"
                label={t("events:guests.bibNumber", { defaultValue: "BIB number" })}
                // #171 — a bib is also a bracelet number, on any event; their tickets' numbers are listed (each is edited
                // on its ticket — Assign BIB on the guest list, or the box office), never copied into this field.
                description={
                  (guest?.ticketBibs ?? []).length > 0
                    ? t("events:guests.bibNumberWithTickets", {
                        defaultValue: "Several numbers: separate with commas. On their tickets: {{list}}.",
                        list: (guest?.ticketBibs ?? []).map((tb) => (tb.ticketTypeName ? `${tb.bibNumber} (${tb.ticketTypeName})` : tb.bibNumber)).join(", "),
                      })
                    : t("events:guests.bibNumberHintAny", { defaultValue: "A race number, a bracelet… Several: separate with commas." })
                }
                placeholder={t("events:guests.bibNumberPh", { defaultValue: "e.g. 1042" })}
                value={form.bibNumber}
                onChange={handleChange}
                error={fieldErrors.bibNumber}
              />
              <TextField
                name="accessPassword"
                label={t("events:guests.accessPassword", { defaultValue: "Access password" })}
                description={t("events:guests.accessPasswordHint", { defaultValue: "Optional password for this guest's private event page." })}
                value={form.accessPassword}
                onChange={handleChange}
                error={fieldErrors.accessPassword}
              />
            </Stack>
          </Collapse>
        </div>

        <Divider />

        <div>
          <Text fw={600} size="md" mb="xs">
            {t("events:guests.groups.title", { defaultValue: "Groups" })}
          </Text>
          <GuestGroupAssignments
            eventId={eventId}
            value={form.guestGroupIds}
            onChange={(ids) => handleChange("guestGroupIds", ids)}
          />

          {/* One message covers the whole set of group pickers — it has no single input. */}
          {fieldErrors.guestGroupIds ? <Input.Error mt={6}>{fieldErrors.guestGroupIds}</Input.Error> : null}
        </div>

        <Divider />

        <div>
          <Text fw={600} size="md" mb="xs">
            {t("events:guests.rsvpGroup", { defaultValue: "RSVP group" })}
          </Text>
          <SelectField
            name="rsvpGroupId"
            description={t("events:guests.rsvpGroupHint", {
              defaultValue: "Who they RSVP'd with. Create groups by selecting guests on the list.",
            })}
            placeholder={t("events:guests.rsvpGroupNone", { defaultValue: "No group" })}
            data={rsvpGroupOptions}
            value={form.rsvpGroupId}
            onChange={(_n, v) => handleChange("rsvpGroupId", (v as string) ?? null)}
            clearable
            searchable
            createLabel={canCreateRsvpGroup ? t("events:guests.rsvpGroupCreate", { defaultValue: "Create an RSVP group" }) : undefined}
            onCreate={canCreateRsvpGroup ? () => setRsvpGroupCreateOpen(true) : undefined}
            error={fieldErrors.rsvpGroupId}
          />

          <QuickCreateDrawer
            opened={rsvpGroupCreateOpen}
            onClose={() => setRsvpGroupCreateOpen(false)}
            title={t("events:guests.rsvpGroupCreate", { defaultValue: "Create an RSVP group" })}
            description={t("events:guests.rsvpGroupCreateHint", {
              defaultValue: "Guests in one group RSVP together — name it after the household or party.",
            })}
            label={t("events:guests.rsvpGroupName", { defaultValue: "Group name" })}
            placeholder={t("events:guests.rsvpGroupNamePh", { defaultValue: "e.g. The Smiths" })}
            onSubmit={(name) => RsvpGroupService.create({ eventId, name })}
            onCreated={async (id) => {
              await reloadRsvpGroups();
              handleChange("rsvpGroupId", id);
            }}
          />
          {form.rsvpGroupId && (
            <SwitchField
              name="isGroupPrimary"
              mt="sm"
              label={t("events:guests.rsvpGroupPrimary", { defaultValue: "Main guest of this group" })}
              checked={form.isGroupPrimary}
              onChange={handleChange}
              error={fieldErrors.isGroupPrimary}
            />
          )}

          <NumberField
            name="rsvpGroupSize"
            mt="sm"
            label={t("events:guests.rsvpGroupSize", { defaultValue: "Max RSVP group size" })}
            description={t("events:guests.rsvpGroupSizeHint", {
              defaultValue: "Most people this guest can RSVP for (their household). Leave empty to use the event default.",
            })}
            value={form.rsvpGroupSize}
            onChange={handleChange}
            min={1}
            allowDecimal={false}
            error={fieldErrors.rsvpGroupSize}
          />

          {isEdit && guest?.id && form.rsvpGroupId && (
            <Button
              mt="sm"
              variant="light"
              color="gray"
              leftSection={<AppIcon iconName="Users" size={16} />}
              onClick={() => setMembersOpen(true)}
            >
              {t("events:guests.rsvpGroupMembers", { defaultValue: "See group members" })}
            </Button>
          )}

          {/* #62 P6c Phase B — the family's other adults, each with their own link to this invitation. */}
          {isEdit && guest?.id ? (
            <Box mt="md">
              <LinkedAdultsSection eventId={eventId} eventGuestId={guest.id} />
            </Box>
          ) : null}
        </div>

        {isEdit && guest && (onOpenAnswers || onOpenNotes) && (
          <>
            <Divider />
            <Group grow align="flex-start">
              {onOpenAnswers && (
                <Button
                  variant="light"
                  leftSection={<AppIcon iconName="ClipboardList" size={16} />}
                  onClick={() => onOpenAnswers(guest)}
                >
                  {t("events:guests.answerQuestions", { defaultValue: "Registration answers" })}
                </Button>
              )}
              {onOpenNotes && (
                <Button
                  variant="light"
                  color="gray"
                  leftSection={<AppIcon iconName="Notebook" size={16} />}
                  onClick={() => onOpenNotes(guest)}
                >
                  {t("events:guests.notes.action", { defaultValue: "Notes" })}
                </Button>
              )}
            </Group>
          </>
        )}
      </Stack>

      {isEdit && guest?.id && form.rsvpGroupId && (
        <RsvpGroupMembersDrawer
          opened={membersOpen}
          onClose={() => setMembersOpen(false)}
          eventId={eventId}
          rsvpGroupId={form.rsvpGroupId}
          groupName={rsvpGroupName}
          onChanged={() => onSaved(guest ?? undefined)}
        />
      )}

    </AppDrawer>
  );
};
